import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Search, UserPlus, CheckCircle, AlertCircle,
  Shield, Star, Phone, Mail, User, Loader2, Send, Clock,
  Sparkles, Leaf, Waves, Wind, Wrench, ChevronRight, Lock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  cat_diarista: { icon: Sparkles, name: "Diarista", color: "bg-pink-100 text-pink-600" },
  cat_jardinagem: { icon: Leaf, name: "Jardinagem", color: "bg-green-100 text-green-600" },
  cat_piscina: { icon: Waves, name: "Piscina", color: "bg-blue-100 text-blue-600" },
  cat_arcondicionado: { icon: Wind, name: "Ar-Condicionado", color: "bg-cyan-100 text-cyan-600" },
  cat_reforma: { icon: Wrench, name: "Reforma Leve", color: "bg-orange-100 text-orange-600" }
};

const SEARCH_TYPES = [
  { id: "name", label: "Nome", placeholder: "Digite o nome do profissional" },
  { id: "email", label: "Email", placeholder: "email@exemplo.com" },
  { id: "phone", label: "Telefone", placeholder: "(11) 99999-9999" },
  { id: "cpf", label: "CPF", placeholder: "000.000.000-00" },
  { id: "cnpj", label: "CNPJ", placeholder: "00.000.000/0000-00" }
];

export default function DirectProviderPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("search");
  const [searchType, setSearchType] = useState("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // Invite form
  const [inviteData, setInviteData] = useState({
    name: "",
    email: "",
    phone: "",
    cpf_cnpj: ""
  });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const searchProviders = async () => {
    if (!searchQuery.trim()) {
      toast.error("Digite algo para buscar");
      return;
    }

    setSearching(true);
    setSearchResults([]);

    try {
      const response = await fetch(`${API_URL}/api/providers/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          query: searchQuery,
          search_type: searchType
        })
      });

      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
        if (results.length === 0) {
          toast.info("Nenhum prestador encontrado. Você pode convidá-lo para a plataforma.");
        }
      }
    } catch (error) {
      toast.error("Erro ao buscar prestadores");
    } finally {
      setSearching(false);
    }
  };

  const inviteProvider = async () => {
    if (!inviteData.name) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!inviteData.email && !inviteData.phone) {
      toast.error("Email ou telefone é obrigatório");
      return;
    }

    setInviting(true);
    try {
      const response = await fetch(`${API_URL}/api/providers/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });

      if (response.ok) {
        const result = await response.json();
        setInviteResult(result);
        toast.success("Convite enviado com sucesso!");
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Erro ao enviar convite");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const selectProviderAndContinue = (provider) => {
    setSelectedProvider(provider);
    // Navigate to next step - category selection or direct service creation
    if (selectedCategory) {
      navigate(`/direct-service/${provider.provider_id}/${selectedCategory}`);
    }
  };

  const proceedWithCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    if (selectedProvider) {
      navigate(`/direct-service/${selectedProvider.provider_id}/${categoryId}`);
    } else if (inviteResult) {
      navigate(`/direct-service/${inviteResult.provider_id}/${categoryId}`);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="direct-provider-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/discovery")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-slate-900">Já Tenho um Profissional</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Hero Banner */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-xl font-bold mb-2">Use o eZelo. para pagar com segurança</h1>
                <p className="text-slate-300 text-sm mb-3">
                  Mesmo que você já conheça o profissional, nossa plataforma garante:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Contrato digital</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Escrow protegido</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Auditoria IA</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span>Disputa mediada</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step Indicator */}
        {!selectedProvider && !inviteResult && (
          <div className="flex items-center justify-center gap-4 py-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
              <span className="text-sm font-medium">Identificar Profissional</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 font-bold">2</div>
              <span className="text-sm text-slate-500">Selecionar Serviço</span>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 font-bold">3</div>
              <span className="text-sm text-slate-500">Criar Contrato</span>
            </div>
          </div>
        )}

        {/* Main Content - Search or Invite */}
        {!selectedProvider && !inviteResult ? (
          <Card>
            <CardHeader>
              <CardTitle>Identificar Profissional</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="search" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                    <Search className="w-4 h-4 mr-2" />
                    Buscar Cadastrado
                  </TabsTrigger>
                  <TabsTrigger value="invite" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Convidar Externo
                  </TabsTrigger>
                </TabsList>

                {/* Search Tab */}
                <TabsContent value="search" className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Busque por um profissional que já está cadastrado na plataforma.
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {SEARCH_TYPES.map((type) => (
                      <Button
                        key={type.id}
                        variant={searchType === type.id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSearchType(type.id)}
                        className={searchType === type.id ? "bg-slate-900" : ""}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>

                  <div className="flex gap-3">
                    <Input
                      placeholder={SEARCH_TYPES.find(t => t.id === searchType)?.placeholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && searchProviders()}
                      className="flex-1"
                      data-testid="search-input"
                    />
                    <Button onClick={searchProviders} disabled={searching} data-testid="search-btn">
                      {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="space-y-3 mt-4">
                      <p className="text-sm font-medium text-slate-700">{searchResults.length} profissional(is) encontrado(s):</p>
                      {searchResults.map((provider) => (
                        <div
                          key={provider.provider_id}
                          className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-emerald-300 cursor-pointer transition-colors"
                          onClick={() => selectProviderAndContinue(provider)}
                          data-testid={`provider-result-${provider.provider_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-slate-500" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">{provider.user?.name || "Prestador"}</span>
                                {provider.is_verified && (
                                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-slate-500">
                                {provider.average_rating > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                    {provider.average_rating.toFixed(1)}
                                  </span>
                                )}
                                <span>{provider.total_jobs || 0} serviços</span>
                              </div>
                            </div>
                          </div>
                          <Badge className={provider.is_verified ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                            {provider.is_verified ? "Verificado" : "Não verificado"}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Invite Tab */}
                <TabsContent value="invite" className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Como funciona o convite?</p>
                        <p className="text-sm text-amber-700">
                          O profissional receberá um convite por email/WhatsApp para se cadastrar. 
                          Ele precisará aceitar os termos e cadastrar dados bancários antes de receber pagamentos.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="invite-name">Nome Completo *</Label>
                      <Input
                        id="invite-name"
                        placeholder="Nome do profissional"
                        value={inviteData.name}
                        onChange={(e) => setInviteData({...inviteData, name: e.target.value})}
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="invite-email">Email</Label>
                        <Input
                          id="invite-email"
                          type="email"
                          placeholder="email@exemplo.com"
                          value={inviteData.email}
                          onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                          data-testid="invite-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="invite-phone">Telefone</Label>
                        <Input
                          id="invite-phone"
                          placeholder="(11) 99999-9999"
                          value={inviteData.phone}
                          onChange={(e) => setInviteData({...inviteData, phone: e.target.value})}
                          data-testid="invite-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="invite-cpf">CPF ou CNPJ (opcional)</Label>
                      <Input
                        id="invite-cpf"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        value={inviteData.cpf_cnpj}
                        onChange={(e) => setInviteData({...inviteData, cpf_cnpj: e.target.value})}
                      />
                    </div>

                    <Button 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      onClick={inviteProvider}
                      disabled={inviting}
                      data-testid="invite-btn"
                    >
                      {inviting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar Convite
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        ) : (
          /* Provider Selected - Show Category Selection */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Profissional Selecionado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Selected Provider Info */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <User className="w-7 h-7 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">
                      {selectedProvider?.user?.name || inviteResult?.message?.split(" para ")[1] || inviteData.name}
                    </p>
                    {inviteResult && (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Clock className="w-4 h-4" />
                        <span>Aguardando aceite do convite</span>
                      </div>
                    )}
                    {selectedProvider?.is_verified && (
                      <Badge className="bg-emerald-100 text-emerald-700 mt-1">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verificado
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">Selecione o tipo de serviço:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {categories.map((category) => {
                    const config = CATEGORY_CONFIG[category.category_id];
                    const Icon = config?.icon || Sparkles;
                    return (
                      <button
                        key={category.category_id}
                        onClick={() => proceedWithCategory(category.category_id)}
                        className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all hover:border-emerald-300 ${
                          selectedCategory === category.category_id 
                            ? "border-emerald-500 bg-emerald-50" 
                            : "border-slate-200"
                        }`}
                        data-testid={`category-${category.category_id}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config?.color || "bg-slate-100"}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{category.name}</p>
                          <p className="text-xs text-slate-500">{category.budgeting_mode === "remote" ? "Orçamento Remoto" : "Visita Presencial"}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trust Banner */}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Lock className="w-10 h-10 text-emerald-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-lg mb-2">O dinheiro só é liberado após a aprovação do serviço</h3>
                <p className="text-slate-300 text-sm">
                  Contrato e pagamento protegidos pela plataforma. Evite riscos. Use escrow.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
